import { useMemo } from 'react'
import { ColumnDef } from '@atbay/cyber-components'
import { CustomCheckbox } from '@atbay/component-lib'
import { useCustomTheme } from '@/core/theme'
import SearchInput from '@/ui/components/search-input/SearchInput'
import Typography from '@/ui/components/typography'
import { useAffectedAssetsTable } from './useAffectedAssetsTable'
import {
    AssetsTable,
    SearchWrapper,
    TableSection,
} from './AffectedAssetsTable.styles'
import {
    AffectedAssetRow,
    AffectedAssetsTableProps,
} from './AffectedAssetsTable.types'

const AffectedAssetsTable = <T extends AffectedAssetRow>({
    rows,
    columns,
    selectedIds,
    onSelectionChange,
    disabled = false,
    title,
    searchable = false,
    searchKeys = [],
    searchPlaceholder = 'Search assets...',
    emptyStateMessage = 'No matching assets found',
    testIdPrefix = 'affected-assets',
}: AffectedAssetsTableProps<T>) => {
    const theme = useCustomTheme()
    const {
        wrapperRef,
        search,
        setSearch,
        sorting,
        setSorting,
        visibleRows,
        tableHeight,
        rowHeight,
        isAllSelected,
        toggleAll,
        toggleRow,
    } = useAffectedAssetsTable<T>({
        rows,
        selectedIds,
        onSelectionChange,
        disabled,
        searchable,
        searchKeys,
    })

    const tableColumns = useMemo<ColumnDef<T>[]>(
        () => [
            {
                id: 'selection',
                header: () => (
                    <CustomCheckbox
                        checked={isAllSelected}
                        onChange={toggleAll}
                        disabled={disabled}
                        accessibilityId={`${testIdPrefix}-select-all`}
                    />
                ),
                cell: ({ row }) => (
                    <CustomCheckbox
                        checked={selectedIds.includes(row.original.id)}
                        onChange={() => toggleRow(row.original.id)}
                        disabled={disabled}
                        accessibilityId={`${testIdPrefix}-select-${row.original.id}`}
                    />
                ),
                enableSorting: false,
                width: '48px',
            } as ColumnDef<T>,
            ...columns,
        ],
        [
            columns,
            disabled,
            isAllSelected,
            selectedIds,
            testIdPrefix,
            toggleAll,
            toggleRow,
        ]
    )

    return (
        <TableSection ref={wrapperRef} data-testid={`${testIdPrefix}-table`}>
            <Typography
                variant="s-semibold"
                color={theme.colors.greys[800]}
                testId={`${testIdPrefix}-title`}
            >
                {title ?? `Affected Assets (${rows.length})`}
            </Typography>
            {searchable && (
                <SearchWrapper data-testid={`${testIdPrefix}-search`}>
                    <SearchInput
                        value={search}
                        placeholder={searchPlaceholder}
                        onChange={setSearch}
                        dataTestId={`${testIdPrefix}-search-input`}
                    />
                </SearchWrapper>
            )}
            <AssetsTable
                columns={tableColumns}
                data={visibleRows}
                sorting={sorting}
                setSorting={setSorting}
                getRowId={(row: T) => row.id}
                rowHeight={rowHeight}
                tableHeight={tableHeight}
                emptyState={emptyStateMessage}
            />
        </TableSection>
    )
}

export default AffectedAssetsTable
